import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Alert.module.css';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  /** Overrides the native `title` (tooltip) attribute — Alert's heading line
   *  needs to accept markup, not just a string. */
  title?: ReactNode;
  /** Overrides the variant's default icon. Pass `false` to render none. */
  icon?: ReactNode | false;
}

function DefaultIcon({ variant }: { variant: AlertVariant }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
  };
  switch (variant) {
    case 'success':
      return (
        <svg {...common}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...common}>
          <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </svg>
      );
    case 'error':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M15 9l-6 6M9 9l6 6" />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8h.01M11 12h1v5h1" />
        </svg>
      );
  }
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { variant = 'info', title, icon, className, children, role = 'status', ...rest },
  ref,
) {
  return (
    <div ref={ref} role={role} className={cx(styles.alert, styles[variant], className)} {...rest}>
      {icon !== false && (
        <span className={styles.icon}>{icon ?? <DefaultIcon variant={variant} />}</span>
      )}
      <div className={styles.body}>
        {title && <span className={styles.title}>{title}</span>}
        {children && <span className={styles.message}>{children}</span>}
      </div>
    </div>
  );
});
